import { useModal } from 'modules/modal/hooks/useModal';
import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IconButton } from 'shared/components/IconButton/IconButton';
import { useApplicationCountStore } from 'shared/hooks/stores/useApplicationCountStore';
import { useExpertProfileStore } from 'shared/hooks/stores/useExpertProfileStore';
import { useOnboardingStore } from 'shared/hooks/stores/useOnboardingStore';
import { useApplicationSearchParam } from 'shared/hooks/useApplicationSearchParam';
import { useBreakpoint } from 'shared/hooks/useBreakpoint';
import { useSitemap } from 'shared/hooks/useSitemap';
import { BusinessIcon, CrossIcon, DocumentIcon, EyeIcon, GearIcon, UserIcon } from 'shared/icons';
import { ConnectisLogo } from 'shared/icons/ConnectisLogo';
import { ConnectisPrimaryLogo } from 'shared/icons/ConnectisPrimaryLogo';
import { mergeClasses } from 'shared/utils/mergeClasses';
import { useShallow } from 'zustand/react/shallow';
import { useApplicationCountQuery } from '../../../../../shared/hooks/api/applicationCount/applicationCount.generated';
import { SignOutModal } from '../SignOutModal';
import ellipseCenterSrc from './assets/ellipse-center.svg';
import ellipseTopSrc from './assets/ellipse-top.svg';
import { Menu, MenuPath } from './subcomponents/Menu';
import { SignOutButton } from './subcomponents/SignOutButton';
import { UserBadge } from './subcomponents/UserBadge';

interface NavbarProps {
  open: boolean;
  onClose: () => void;
}

export const Navbar: FC<NavbarProps> = memo(({ open, onClose }) => {
  const { t } = useTranslation();
  const sitemap = useSitemap();
  const { openModal } = useModal();
  const isMobile = useBreakpoint('lg');
  const { isApplicationParamPresent } = useApplicationSearchParam();
  const unviewedApplicationCount = useApplicationCountStore((store) => store.count);

  const { firstName, lastName, jobPositionId, avatarUrl } = useExpertProfileStore(
    useShallow(({ expertProfile }) => ({
      firstName: expertProfile?.firstName || '',
      lastName: expertProfile?.lastName || '',
      jobPositionId: expertProfile?.jobPositionId || '',
      avatarUrl: expertProfile?.avatarUrl || '',
    })),
  );
  const onboardingFinishedAt = useOnboardingStore((state) => state.finishedAt);

  const { data: applicationCount } = useApplicationCountQuery({ skip: !!onboardingFinishedAt });

  const paths: MenuPath[] = useMemo(
    () => [
      {
        href: sitemap.offers,
        label: t('pageTitles:offers'),
        icon: BusinessIcon,
        activePaths: isApplicationParamPresent
          ? [sitemap.offers, sitemap.notifications, sitemap.observed]
          : [sitemap.offers, sitemap.jobOrder(''), sitemap.notifications, sitemap.observed],
      },
      ...(onboardingFinishedAt || applicationCount
        ? [
            {
              href: sitemap.applications(),
              label: t('pageTitles:applications'),
              icon: DocumentIcon,
              activeQueryParam: 'application',
              count: unviewedApplicationCount,
            },
          ]
        : []),
      ...(onboardingFinishedAt
        ? [
            {
              href: sitemap.profile(),
              label: t('pageTitles:profile'),
              icon: UserIcon,
            },
          ]
        : []),
      {
        href: sitemap.settings,
        label: t('pageTitles:settings'),
        icon: GearIcon,
      },
      ...(isMobile
        ? [
            {
              href: sitemap.observed,
              label: t('pageTitles:observed'),
              icon: EyeIcon,
            },
          ]
        : []),
    ],
    [
      sitemap,
      onboardingFinishedAt,
      t,
      isMobile,
      isApplicationParamPresent,
      unviewedApplicationCount,
    ],
  );

  return (
    <div
      className={mergeClasses(
        'fixed left-0 top-0 z-50 flex h-full-device w-full flex-col space-y-8 overflow-hidden bg-custom-gradient px-4 pb-14 pt-3 transition-all md:px-6 lg:left-4 lg:top-4 lg:h-[calc(100dvh-32px)] lg:max-w-[240px] lg:space-y-14 lg:rounded-2xl lg:bg-lightBlue lg:bg-none lg:px-2 lg:pt-8 lg:transition-none',
        open
          ? 'opacity-100'
          : 'pointer-events-none opacity-0 lg:pointer-events-auto lg:opacity-100',
      )}
    >
      <div className="flex items-center justify-between lg:justify-center">
        <Link className="hidden lg:block" to={sitemap.main}>
          <ConnectisPrimaryLogo className="w-[136px]" />
        </Link>
        <Link className="block lg:hidden" to={sitemap.main}>
          <ConnectisLogo className="w-[120px]" />
        </Link>
        <IconButton
          className="flex lg:hidden"
          name="closeNavbarButton"
          icon={CrossIcon}
          onClick={onClose}
        />
      </div>
      <UserBadge
        firstName={firstName}
        lastName={lastName}
        jobPositionId={jobPositionId}
        avatarUrl={avatarUrl}
      />
      <div className="flex h-full flex-col justify-between">
        <Menu paths={paths} onClose={onClose} />
        <div className="pt-4 lg:border-t lg:border-t-white">
          <SignOutButton
            onClick={() =>
              openModal('signOutModal', (closeModal) => <SignOutModal onClose={closeModal} />)
            }
          />
        </div>
      </div>
      <img
        className="pointer-events-none absolute left-0 top-0 -z-10 !m-0 h-full w-full lg:hidden"
        alt=""
        src={ellipseTopSrc}
      />
      <img
        className="pointer-events-none absolute bottom-0 left-0 right-0 top-0 -z-10 !m-auto h-full w-full lg:hidden"
        alt=""
        src={ellipseCenterSrc}
      />
    </div>
  );
});
